/**
 * AssistenteCompras - Aba do Assistente no módulo Compras
 * Pendências organizadas por área (Cotações / Pedidos) e por tipo de demanda,
 * com ação de resolução em 1 clique, resumo executivo por IA e prazos configuráveis.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import InteligenciaNegocio from './InteligenciaNegocio';
import CustoPorProjeto from './CustoPorProjeto';
import {
  Bot, Settings, Sparkles, Loader2, RefreshCw, FileText, ShoppingCart,
  Package, DollarSign, Link2, Clock, CheckCircle2, FileCheck2, AlertTriangle,
} from 'lucide-react';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

/* Definição dos grupos de demanda, separados por área */
const GRUPOS = {
  cotacoes: [
    {
      tipo: 'cotacao_sem_pedido',
      label: 'Aprovadas pendentes de gerar pedido',
      descricao: 'Cotações já aprovadas cujo pedido de compra ainda não foi gerado',
      icon: FileCheck2,
      accent: 'text-emerald-700 bg-emerald-100',
    },
    {
      tipo: 'cotacao_sem_resposta',
      label: 'Sem nenhuma resposta de fornecedor',
      descricao: 'Cotações enviadas aguardando retorno — talvez valha cobrar o fornecedor',
      icon: Clock,
      accent: 'text-amber-700 bg-amber-100',
    },
  ],
  pedidos: [
    {
      tipo: 'projeto_orcamento_estourado',
      label: 'Projetos com orçamento estourado',
      descricao: 'Projetos cujo total gasto em pedidos passou do custo estimado da ficha técnica',
      icon: AlertTriangle,
      accent: 'text-rose-700 bg-rose-100',
    },
    {
      tipo: 'custos_nao_conferidos',
      label: 'Sem custos conferidos no projeto',
      descricao: 'Pedidos vinculados a projetos cujos custos ainda não foram distribuídos na ficha técnica',
      icon: Link2,
      accent: 'text-red-700 bg-red-100',
    },
    {
      tipo: 'pedido_sem_recebimento',
      label: 'Pendentes de dar entrada no estoque',
      descricao: 'Pedidos enviados ao fornecedor sem registro de recebimento',
      icon: Package,
      accent: 'text-orange-700 bg-orange-100',
    },
    {
      tipo: 'parcelas_conta_azul',
      label: 'Parcelas pagas sem envio ao Conta Azul',
      descricao: 'Despesas pagas que não foram sincronizadas com o financeiro',
      icon: DollarSign,
      accent: 'text-sky-700 bg-sky-100',
    },
  ],
};

const CONFIG_CAMPOS = [
  { key: 'dias_cotacao_sem_pedido', label: 'Cotação aprovada sem pedido gerado (dias)' },
  { key: 'dias_cotacao_sem_resposta', label: 'Cotação sem resposta de fornecedor (dias)' },
  { key: 'dias_pedido_sem_recebimento', label: 'Pedido enviado sem recebimento (dias)' },
  { key: 'dias_custos_nao_conferidos', label: 'Custos de projeto não conferidos (dias)' },
];

/* ---------- Card de um grupo de demanda ---------- */
const GrupoDemanda = ({ grupo, itens, onAcao }) => {
  const Icon = grupo.icon;
  const vazio = itens.length === 0;
  return (
    <Card className={`border-slate-200 ${vazio ? 'opacity-70' : ''}`} data-testid={`assistente-grupo-${grupo.tipo}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-2.5 mb-1">
          <span className={`p-1.5 rounded-lg shrink-0 ${grupo.accent}`}>
            <Icon className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-slate-900">{grupo.label}</h4>
              {vazio ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">
                  <CheckCircle2 className="w-3 h-3 mr-0.5" /> em dia
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]" data-testid={`assistente-count-${grupo.tipo}`}>
                  {itens.length}
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-slate-500">{grupo.descricao}</p>
          </div>
        </div>

        {!vazio && (
          <div className="space-y-1.5 mt-3 max-h-64 overflow-y-auto pr-1">
            {itens.map((p, idx) => (
              <div
                key={`${p.recurso_id}-${idx}`}
                className="flex items-center gap-3 flex-wrap p-2.5 rounded-lg bg-slate-50 border border-slate-100 hover:border-slate-300 transition-colors"
                data-testid={`assistente-pendencia-${p.tipo}-${p.numero}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {p.numero}
                    {p.valor != null && p.valor > 0 && (
                      <span className="ml-2 text-xs font-normal text-slate-600">{fmtBRL(p.valor)}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {p.detalhe}
                    {p.dias != null && (
                      <span className={`${p.detalhe ? 'ml-1 ' : ''}font-semibold ${p.dias > 30 ? 'text-red-600' : 'text-slate-600'}`}>
                        {p.detalhe ? '· ' : ''}há {p.dias} dia{p.dias !== 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="h-7 text-xs shrink-0 bg-slate-900 hover:bg-slate-700"
                  onClick={() => onAcao && onAcao(p)}
                  data-testid={`assistente-acao-${p.tipo}-${p.numero}`}
                >
                  {p.acao_label}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ---------- Coluna de área (Cotações / Pedidos) ---------- */
const ColunaArea = ({ titulo, icon: Icon, grupos, pendencias, onAcao, testId }) => {
  const totalArea = grupos.reduce(
    (s, g) => s + pendencias.filter((p) => p.tipo === g.tipo).length, 0
  );
  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center gap-2 pb-1 border-b border-slate-200">
        <Icon className="w-4 h-4 text-slate-600" />
        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-[0.15em]">{titulo}</h3>
        <Badge
          className={`text-[10px] ${totalArea > 0 ? 'bg-red-100 text-red-700 hover:bg-red-100' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}`}
        >
          {totalArea} pendência{totalArea !== 1 ? 's' : ''}
        </Badge>
      </div>
      {grupos.map((g) => (
        <GrupoDemanda
          key={g.tipo}
          grupo={g}
          itens={pendencias.filter((p) => p.tipo === g.tipo)}
          onAcao={onAcao}
        />
      ))}
    </div>
  );
};

const AssistenteCompras = ({ onAcao, onTotalChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState('');
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [focusProjeto, setFocusProjeto] = useState(null);

  // Intercepta a ação: estouro de orçamento foca a seção Custo por Projeto (mesmo tab)
  const handleAcaoWrapper = (p) => {
    if (p?.acao === 'ver_custo_projeto') {
      // força re-trigger mesmo se for o mesmo projeto
      setFocusProjeto(null);
      setTimeout(() => setFocusProjeto(p.recurso_id), 0);
      return;
    }
    if (onAcao) onAcao(p);
  };

  const fetchPendencias = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/compras/assistente/pendencias');
      setData(res.data);
      setConfigForm(res.data.config || {});
      if (onTotalChange) onTotalChange(res.data.total || 0);
    } catch {
      /* silencioso — painel é auxiliar */
    } finally {
      setLoading(false);
    }
  }, [onTotalChange]);

  useEffect(() => { fetchPendencias(); }, [fetchPendencias]);

  const handleResumo = async () => {
    setLoadingResumo(true);
    try {
      const res = await api.post('/compras/assistente/resumo', {});
      setResumo(res.data.resumo);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar resumo com IA');
    } finally {
      setLoadingResumo(false);
    }
  };

  const handleSalvarConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/compras/assistente/config', configForm);
      toast.success('Prazos do assistente atualizados');
      setShowConfig(false);
      fetchPendencias();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSavingConfig(false);
    }
  };

  const pendencias = data?.pendencias || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-5" data-testid="assistente-compras-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="p-2 rounded-xl bg-indigo-100 text-indigo-700">
            <Bot className="w-5 h-5" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              Assistente de Compras
              {total > 0 ? (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid="assistente-badge-total">
                  {total} pendência{total > 1 ? 's' : ''}
                </Badge>
              ) : (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="assistente-badge-ok">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Tudo em dia
                </Badge>
              )}
            </h2>
            <p className="text-xs text-slate-500">
              Monitora cotações, pedidos, vínculos com projetos e financeiro — e aponta o que está travado.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {total > 0 && !resumo && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleResumo}
              disabled={loadingResumo}
              className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
              data-testid="assistente-gerar-resumo"
            >
              {loadingResumo ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Gerar resumo com IA
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={fetchPendencias} title="Atualizar" data-testid="assistente-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowConfig(true)} title="Configurar prazos das regras" data-testid="assistente-config-btn">
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Resumo IA */}
      {resumo && (
        <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-100 text-sm text-slate-700" data-testid="assistente-resumo">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Resumo do Assistente
          </p>
          <p className="italic">{resumo}</p>
        </div>
      )}

      {/* Corpo */}
      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : total === 0 ? (
        <Card className="border-emerald-200">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">Nenhum processo travado no módulo de Compras</p>
            <p className="text-slate-500 text-sm mt-1">O assistente continua monitorando cotações, pedidos e financeiro.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ColunaArea
            titulo="Cotações"
            icon={FileText}
            grupos={GRUPOS.cotacoes}
            pendencias={pendencias}
            onAcao={handleAcaoWrapper}
            testId="assistente-area-cotacoes"
          />
          <ColunaArea
            titulo="Pedidos"
            icon={ShoppingCart}
            grupos={GRUPOS.pedidos}
            pendencias={pendencias}
            onAcao={handleAcaoWrapper}
            testId="assistente-area-pedidos"
          />
        </div>
      )}

      {/* Inteligência do Negócio */}
      <InteligenciaNegocio />

      {/* Custo por Projeto */}
      <div className="pt-2 border-t border-slate-200">
        <CustoPorProjeto focusProjetoId={focusProjeto} />
      </div>

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md" data-testid="assistente-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-4 h-4" /> Prazos do Assistente
            </DialogTitle>
            <DialogDescription>
              Defina após quantos dias cada situação vira uma pendência.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {CONFIG_CAMPOS.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-3">
                <Label className="text-xs text-slate-600 flex-1">{c.label}</Label>
                <Input
                  type="number" min="0"
                  className="w-20 h-8 text-sm"
                  value={configForm[c.key] ?? ''}
                  onChange={(e) => setConfigForm((prev) => ({ ...prev, [c.key]: e.target.value }))}
                  data-testid={`assistente-config-${c.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={handleSalvarConfig} disabled={savingConfig} data-testid="assistente-config-salvar">
              {savingConfig && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssistenteCompras;
